import type { RecategorizeEligibleTransaction, RecategorizeProposal } from './types';

export const validateRecategorizeProposals = (
  eligibleTransactions: RecategorizeEligibleTransaction[],
  parsedResponse: unknown,
): RecategorizeProposal[] => {
  if (!parsedResponse || typeof parsedResponse !== 'object') {
    return [];
  }

  const rawProposals = (parsedResponse as Record<string, unknown>).proposals;
  if (!Array.isArray(rawProposals)) {
    return [];
  }

  const eligibleById = new Map(eligibleTransactions.map((transaction) => [transaction.transactionId, transaction]));
  const seenTransactionIds = new Set<string>();
  const proposals: RecategorizeProposal[] = [];

  rawProposals.forEach((rawProposal) => {
    if (!rawProposal || typeof rawProposal !== 'object') {
      return;
    }

    const candidate = rawProposal as Record<string, unknown>;
    const { transactionId, proposedCategoryId, rationale } = candidate;

    if (typeof transactionId !== 'string' || typeof proposedCategoryId !== 'string' || typeof rationale !== 'string') {
      return;
    }

    const eligibleTransaction = eligibleById.get(transactionId);
    if (!eligibleTransaction || seenTransactionIds.has(transactionId)) {
      return;
    }

    if (proposedCategoryId === eligibleTransaction.categoryId) {
      return;
    }

    // The proposed category's display name is always resolved from the allowed-categories list
    // by id, never taken from the model directly - this guarantees what's shown to the user can
    // never diverge from the category actually applied (the id is the only thing validated and
    // the only thing setCoa acts on).
    const allowedCategory = eligibleTransaction.allowedCategories.find(
      (category) => category.id === proposedCategoryId,
    );
    if (!allowedCategory) {
      return;
    }

    const confidence = typeof candidate.confidence === 'number' ? Math.min(1, Math.max(0, candidate.confidence)) : 0;

    seenTransactionIds.add(transactionId);
    proposals.push({
      transactionId,
      currentCategoryName: eligibleTransaction.currentCategoryName,
      proposedCategoryId,
      proposedCategoryName: allowedCategory.name,
      confidence,
      rationale,
    });
  });

  return proposals;
};

export const parseRecategorizeAssistantResponse = (
  eligibleTransactions: RecategorizeEligibleTransaction[],
  assistantResponse: string,
): RecategorizeProposal[] | null => {
  const trimmedAssistantResponse = assistantResponse.trim();
  if (!trimmedAssistantResponse) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmedAssistantResponse);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as Record<string, unknown>).proposals)) {
    return null;
  }

  return validateRecategorizeProposals(eligibleTransactions, parsed);
};
